import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { 
  Users, UserPlus, Edit, Trash2, Mail, Shield, 
  AlertCircle, CheckCircle, X, Search, Filter, MessageCircle 
} from 'lucide-react';
import { MODULE_HIERARCHY, ALL_MODULE_IDS } from '../config/modulePermissions';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // AMC-level enabled modules (set by Owner)
  const amcEnabledModules = new Set(currentUser?.amc_enabled_modules || ALL_MODULE_IDS);

  // Build default perms: all true for AMC-enabled modules
  const buildDefaultPerms = () => {
    const perms = {};
    ALL_MODULE_IDS.forEach(id => { perms[id] = amcEnabledModules.has(id); });
    return perms;
  };

  // Invite form state
  const [inviteForm, setInviteForm] = useState({
    email: '',
    name: '',
    role: 'viewer',
    module_permissions: buildDefaultPerms(),
    email_sending_enabled: false,
    whatsapp_sending_enabled: false,
    can_email_external_recipients: false,
    mobile_country_code: '+91',
    mobile: '',
  });

  // Edit form state
  const [editForm, setEditForm] = useState({
    name: '',
    role: '',
    module_permissions: buildDefaultPerms(),
    is_active: true,
    email_sending_enabled: false,
    whatsapp_sending_enabled: false,
    can_email_external_recipients: false,
    mobile_country_code: '+91',
    mobile: '',
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/users`);
      setUsers(response.data);
    } catch (err) {
      setError('Failed to load users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await axios.post(`${API_URL}/users/invite`, inviteForm);
      setSuccess('User invited successfully! Invitation email sent.');
      setShowInviteModal(false);
      setInviteForm({
        email: '',
        name: '',
        role: 'viewer',
        module_permissions: buildDefaultPerms(),
        email_sending_enabled: false,
        whatsapp_sending_enabled: false,
        mobile_country_code: '+91',
        mobile: '',
      });
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to invite user');
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await axios.put(`${API_URL}/users/${selectedUser.id}`, editForm);
      setSuccess('User updated successfully!');
      setShowEditModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDeactivateUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to deactivate ${userName}? They will not be able to log in, but their data will be preserved.`)) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      await axios.delete(`${API_URL}/users/${userId}?permanent=false`);
      setSuccess('User deactivated successfully!');
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to deactivate user');
    }
  };

  const handlePermanentDeleteUser = async (userId, userName) => {
    const confirmed = window.confirm(
      `⚠️ WARNING: Are you sure you want to PERMANENTLY DELETE ${userName}?\n\n` +
      `This action CANNOT be undone and will:\n` +
      `• Remove all user data from the database\n` +
      `• Delete their account permanently\n\n` +
      `Type "DELETE" in the next prompt to confirm.`
    );
    
    if (!confirmed) {
      return;
    }

    const confirmText = window.prompt(`Type "DELETE" to confirm permanent deletion of ${userName}:`);
    if (confirmText !== 'DELETE') {
      setError('Deletion cancelled - confirmation text did not match');
      return;
    }

    setError('');
    setSuccess('');

    try {
      await axios.delete(`${API_URL}/users/${userId}?permanent=true`);
      setSuccess('User permanently deleted successfully!');
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    const perms = buildDefaultPerms();
    // Override with user's actual permissions
    if (user.module_permissions) {
      Object.keys(user.module_permissions).forEach(k => {
        if (k in perms) perms[k] = user.module_permissions[k];
      });
    }
    setEditForm({
      name: user.name,
      role: user.role,
      module_permissions: perms,
      is_active: user.is_active,
      email_sending_enabled: !!user.email_sending_enabled,
      whatsapp_sending_enabled: !!user.whatsapp_sending_enabled,
      can_email_external_recipients: !!user.can_email_external_recipients,
      mobile_country_code: user.mobile_country_code || '+91',
      mobile: user.mobile || '',
    });
    setShowEditModal(true);
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-800',
      manager: 'bg-blue-100 text-blue-800',
      viewer: 'bg-gray-100 text-gray-800'
    };
    return colors[role] || colors.viewer;
  };

  // Check if current user is admin
  if (currentUser?.role !== 'admin') {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="text-red-600 mr-2 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h3 className="text-red-900 font-semibold">Access Denied</h3>
            <p className="text-red-700 text-sm mt-1">
              You don't have permission to access User Management. Only administrators can manage users.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Users className="mr-3" size={32} />
            User Management
          </h1>
          <p className="text-gray-600 mt-1">Manage users and their permissions</p>
        </div>
        <button
          onClick={() => setShowInviteModal(true)}
          className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors flex items-center"
        >
          <UserPlus size={20} className="mr-2" />
          Invite User
        </button>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start">
          <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-0.5" size={20} />
          <div className="flex-1">
            <span className="text-green-800 text-sm">{success}</span>
          </div>
          <button onClick={() => setSuccess('')} className="text-green-600 hover:text-green-700">
            <X size={16} />
          </button>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
          <AlertCircle className="text-red-600 mr-2 flex-shrink-0 mt-0.5" size={20} />
          <div className="flex-1">
            <span className="text-red-800 text-sm">{error}</span>
          </div>
          <button onClick={() => setError('')} className="text-red-600 hover:text-red-700">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Search and Filter */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:space-x-4 space-y-3 md:space-y-0">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center space-x-2">
            <Filter size={20} className="text-gray-500" />
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Roles</option>
              <option value="admin">Admin</option>
              <option value="manager">Fund Manager</option>
              <option value="viewer">Viewer</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-500 mt-4">Loading users...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="mx-auto text-gray-400 mb-4" size={48} />
            <p className="text-gray-500">No users found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email Verified
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email Sending
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map(user => (
                  <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-blue-600 font-semibold text-sm">
                            {user.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{user.name}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadge(user.role)}`}>
                        {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        user.email_verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {user.email_verified ? 'Verified' : 'Pending'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-testid={`user-email-sending-${user.id}`}>
                      <span className={`px-3 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full ${
                        user.email_sending_enabled ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'
                      }`}>
                        <Mail size={12} className="mr-1" />
                        {user.email_sending_enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-3">
                        <button
                          onClick={() => openEditModal(user)}
                          className="text-blue-600 hover:text-blue-900 inline-flex items-center"
                          title="Edit user"
                        >
                          <Edit size={16} className="mr-1" />
                          Edit
                        </button>
                        {user.id !== currentUser.id && (
                          <>
                            <button
                              onClick={() => handleDeactivateUser(user.id, user.name)}
                              className="text-yellow-600 hover:text-yellow-900 inline-flex items-center"
                              title="Deactivate user (soft delete)"
                            >
                              <Shield size={16} className="mr-1" />
                              Deactivate
                            </button>
                            <button
                              onClick={() => handlePermanentDeleteUser(user.id, user.name)}
                              className="text-red-600 hover:text-red-900 inline-flex items-center"
                              title="Permanently delete user"
                            >
                              <Trash2 size={16} className="mr-1" />
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invite User Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                  <UserPlus className="mr-2" size={24} />
                  Invite New User
                </h2>
                <button
                  onClick={() => setShowInviteModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <form onSubmit={handleInviteUser} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={inviteForm.name}
                    onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mobile Number (for WhatsApp)
                </label>
                <div className="grid grid-cols-[110px,1fr] gap-2">
                  <input
                    type="text"
                    value={inviteForm.mobile_country_code}
                    onChange={(e) => setInviteForm({ ...inviteForm, mobile_country_code: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="+91"
                    data-testid="invite-mobile-country-code"
                  />
                  <input
                    type="tel"
                    value={inviteForm.mobile}
                    onChange={(e) => setInviteForm({ ...inviteForm, mobile: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="9876543210"
                    data-testid="invite-mobile"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Required for receiving WhatsApp Drawdown Reports.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  User Role *
                </label>
                <select
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="viewer">Viewer (View only)</option>
                  <option value="manager">Fund Manager (Add, Edit, Download)</option>
                  <option value="admin">Admin (Full access)</option>
                </select>
              </div>

              <div className="border border-amber-200 bg-amber-50 rounded-lg p-4">
                <label className="flex items-start cursor-pointer" data-testid="invite-toggle-email-sending">
                  <input
                    type="checkbox"
                    checked={inviteForm.email_sending_enabled}
                    onChange={(e) => setInviteForm({ ...inviteForm, email_sending_enabled: e.target.checked })}
                    className="h-4 w-4 mt-0.5 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                  />
                  <div className="ml-3">
                    <span className="text-sm font-semibold text-gray-800 flex items-center">
                      <Mail size={16} className="mr-2 text-amber-600" />
                      Allow this user to send emails from the portal
                    </span>
                    <p className="text-xs text-gray-600 mt-1">
                      When enabled, the user can configure their own SMTP credentials (Gmail / Outlook App Password) and send emails (e.g. drawdown notices) under their own identity.
                    </p>
                  </div>
                </label>
              </div>

              <div className="border border-green-200 bg-green-50 rounded-lg p-4">
                <label className="flex items-start cursor-pointer" data-testid="invite-toggle-whatsapp-sending">
                  <input
                    type="checkbox"
                    checked={inviteForm.whatsapp_sending_enabled}
                    onChange={(e) => setInviteForm({ ...inviteForm, whatsapp_sending_enabled: e.target.checked })}
                    className="h-4 w-4 mt-0.5 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  />
                  <div className="ml-3">
                    <span className="text-sm font-semibold text-gray-800 flex items-center">
                      <MessageCircle size={16} className="mr-2 text-green-600" />
                      Allow this user to send WhatsApp messages from the portal
                    </span>
                    <p className="text-xs text-gray-600 mt-1">
                      When enabled, the user can pick a published WhatsApp template and dispatch drawdown notices, reminders, and acknowledgments via WhatsApp. AMC-level <strong>WhatsApp Configuration</strong> must already be active.
                    </p>
                  </div>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Module Permissions
                </label>
                <p className="text-xs text-gray-500 mb-3">Only modules enabled by the platform owner are shown below.</p>
                <div className="space-y-2">
                  {MODULE_HIERARCHY.filter(mod => amcEnabledModules.has(mod.id)).map(mod => {
                    const hasSubs = mod.submodules.length > 0;
                    const enabledSubs = mod.submodules.filter(s => amcEnabledModules.has(s.id));
                    return (
                      <div key={mod.id} className={`border rounded-lg overflow-hidden ${inviteForm.module_permissions[mod.id] ? 'border-blue-300' : 'border-gray-200'}`}>
                        <label className="flex items-center p-3 hover:bg-gray-50 cursor-pointer" data-testid={`invite-toggle-${mod.id}`}>
                          <input
                            type="checkbox"
                            checked={inviteForm.module_permissions[mod.id] || false}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              const updates = { [mod.id]: checked };
                              if (!checked) enabledSubs.forEach(s => { updates[s.id] = false; });
                              setInviteForm(prev => ({
                                ...prev,
                                module_permissions: { ...prev.module_permissions, ...updates }
                              }));
                            }}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="ml-3 text-sm font-semibold text-gray-700">{mod.label}</span>
                        </label>
                        {hasSubs && inviteForm.module_permissions[mod.id] && enabledSubs.length > 0 && (
                          <div className="border-t border-gray-100 bg-gray-50/50 px-3 py-2 grid grid-cols-2 gap-1">
                            {enabledSubs.map(sub => (
                              <label key={sub.id} className="flex items-center py-1 cursor-pointer" data-testid={`invite-toggle-${sub.id}`}>
                                <input
                                  type="checkbox"
                                  checked={inviteForm.module_permissions[sub.id] || false}
                                  onChange={(e) => setInviteForm(prev => ({
                                    ...prev,
                                    module_permissions: { ...prev.module_permissions, [sub.id]: e.target.checked }
                                  }))}
                                  className="h-3.5 w-3.5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <span className="ml-2 text-sm text-gray-600">{sub.label}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start">
                <Mail className="text-blue-600 mr-2 flex-shrink-0 mt-0.5" size={20} />
                <div className="text-sm text-blue-800">
                  <p className="font-semibold mb-1">Invitation Email</p>
                  <p>An invitation email with verification link and temporary password will be sent to the user's email address.</p>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center"
                >
                  <Mail size={18} className="mr-2" />
                  Send Invitation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                  <Edit className="mr-2" size={24} />
                  Edit User
                </h2>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
              <p className="text-gray-600 mt-1">{selectedUser.email}</p>
            </div>

            <form onSubmit={handleEditUser} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  required
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mobile Number (for WhatsApp)
                </label>
                <div className="grid grid-cols-[110px,1fr] gap-2">
                  <input
                    type="text"
                    value={editForm.mobile_country_code}
                    onChange={(e) => setEditForm({ ...editForm, mobile_country_code: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="+91"
                    data-testid="edit-mobile-country-code"
                  />
                  <input
                    type="tel"
                    value={editForm.mobile}
                    onChange={(e) => setEditForm({ ...editForm, mobile: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="9876543210"
                    data-testid="edit-mobile"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Required for receiving WhatsApp Drawdown Reports.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  User Role
                </label>
                <select
                  value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="viewer">Viewer (View only)</option>
                  <option value="manager">Fund Manager (Add, Edit, Download)</option>
                  <option value="admin">Admin (Full access)</option>
                </select>
              </div>

              <div className="border border-amber-200 bg-amber-50 rounded-lg p-4">
                <label className="flex items-start cursor-pointer" data-testid="edit-toggle-email-sending">
                  <input
                    type="checkbox"
                    checked={editForm.email_sending_enabled}
                    onChange={(e) => setEditForm({ ...editForm, email_sending_enabled: e.target.checked })}
                    className="h-4 w-4 mt-0.5 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                  />
                  <div className="ml-3">
                    <span className="text-sm font-semibold text-gray-800 flex items-center">
                      <Mail size={16} className="mr-2 text-amber-600" />
                      Allow this user to send emails from the portal
                    </span>
                    <p className="text-xs text-gray-600 mt-1">
                      User must save their own SMTP credentials (Gmail / Outlook App Password) under <strong>Email Configuration</strong> before they can send.
                    </p>
                  </div>
                </label>
              </div>

              <div className="border border-green-200 bg-green-50 rounded-lg p-4">
                <label className="flex items-start cursor-pointer" data-testid="edit-toggle-whatsapp-sending">
                  <input
                    type="checkbox"
                    checked={editForm.whatsapp_sending_enabled}
                    onChange={(e) => setEditForm({ ...editForm, whatsapp_sending_enabled: e.target.checked })}
                    className="h-4 w-4 mt-0.5 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  />
                  <div className="ml-3">
                    <span className="text-sm font-semibold text-gray-800 flex items-center">
                      <MessageCircle size={16} className="mr-2 text-green-600" />
                      Allow this user to send WhatsApp messages from the portal
                    </span>
                    <p className="text-xs text-gray-600 mt-1">
                      User can pick a published WhatsApp template and dispatch drawdown notices, reminders, and acknowledgments via WhatsApp. AMC-level <strong>WhatsApp Configuration</strong> must already be active.
                    </p>
                  </div>
                </label>
              </div>

              <div className="border border-purple-200 bg-purple-50 rounded-lg p-4">
                <label className="flex items-start cursor-pointer" data-testid="edit-toggle-email-external">
                  <input
                    type="checkbox"
                    checked={editForm.can_email_external_recipients}
                    onChange={(e) => setEditForm({ ...editForm, can_email_external_recipients: e.target.checked })}
                    className="h-4 w-4 mt-0.5 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  />
                  <div className="ml-3">
                    <span className="text-sm font-semibold text-gray-800">
                      Allow this user to email Drawdown Reports to <em>external</em> addresses
                    </span>
                    <p className="text-xs text-gray-600 mt-1">
                      By default, the Email Report option on the Drawdown Reports tab is restricted to internal team members (users in this AMC). Enable this to let this user add external email addresses (e.g. auditors, partners) as recipients. Admins always have this access.
                    </p>
                  </div>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Module Permissions
                </label>
                <p className="text-xs text-gray-500 mb-3">Only modules enabled by the platform owner are shown below.</p>
                <div className="space-y-2">
                  {MODULE_HIERARCHY.filter(mod => amcEnabledModules.has(mod.id)).map(mod => {
                    const hasSubs = mod.submodules.length > 0;
                    const enabledSubs = mod.submodules.filter(s => amcEnabledModules.has(s.id));
                    return (
                      <div key={mod.id} className={`border rounded-lg overflow-hidden ${editForm.module_permissions[mod.id] ? 'border-blue-300' : 'border-gray-200'}`}>
                        <label className="flex items-center p-3 hover:bg-gray-50 cursor-pointer" data-testid={`edit-toggle-${mod.id}`}>
                          <input
                            type="checkbox"
                            checked={editForm.module_permissions[mod.id] || false}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              const updates = { [mod.id]: checked };
                              if (!checked) enabledSubs.forEach(s => { updates[s.id] = false; });
                              setEditForm(prev => ({
                                ...prev,
                                module_permissions: { ...prev.module_permissions, ...updates }
                              }));
                            }}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="ml-3 text-sm font-semibold text-gray-700">{mod.label}</span>
                        </label>
                        {hasSubs && editForm.module_permissions[mod.id] && enabledSubs.length > 0 && (
                          <div className="border-t border-gray-100 bg-gray-50/50 px-3 py-2 grid grid-cols-2 gap-1">
                            {enabledSubs.map(sub => (
                              <label key={sub.id} className="flex items-center py-1 cursor-pointer" data-testid={`edit-toggle-${sub.id}`}>
                                <input
                                  type="checkbox"
                                  checked={editForm.module_permissions[sub.id] || false}
                                  onChange={(e) => setEditForm(prev => ({
                                    ...prev,
                                    module_permissions: { ...prev.module_permissions, [sub.id]: e.target.checked }
                                  }))}
                                  className="h-3.5 w-3.5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <span className="ml-2 text-sm text-gray-600">{sub.label}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editForm.is_active}
                    onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-700">Active User</span>
                </label>
                <p className="mt-1 text-xs text-gray-500 ml-7">
                  Uncheck to deactivate user account
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
